/**
 * Script Generator Constants
 * ==========================
 * Shared constants for platforms, tones, categories, and styling.
 */

export const PLATFORMS = [
  { 
    id: "tiktok", 
    name: "TikTok", 
    // Glassmorphism style - foggy/blurry with subtle accent
    color: "bg-gradient-to-br from-zinc-900/90 via-zinc-900/80 to-zinc-800/70 backdrop-blur-xl border border-[#25F4EE]/30", 
    hoverColor: "hover:border-[#25F4EE]/60 hover:shadow-[0_0_40px_rgba(37,244,238,0.15)] hover:from-zinc-900/95",
    textColor: "text-white", 
    accentColor: "#25F4EE",
    accentTextClass: "text-[#25F4EE]",
    duration: "8-10s", 
    style: "Aggressive • Scroll-stopping",
    description: "Stop the scroll with instant hooks and rapid escalation"
  },
  { 
    id: "shorts", 
    name: "YouTube Shorts", 
    // Glassmorphism style - foggy/blurry with subtle red accent
    color: "bg-gradient-to-br from-zinc-900/90 via-zinc-900/80 to-red-950/30 backdrop-blur-xl border border-[#FF0000]/30", 
    hoverColor: "hover:border-[#FF0000]/60 hover:shadow-[0_0_40px_rgba(255,0,0,0.15)] hover:from-zinc-900/95",
    textColor: "text-white", 
    accentColor: "#FF0000",
    accentTextClass: "text-[#FF0000]",
    duration: "9-12s", 
    style: "Thoughtful • Replay-worthy",
    description: "Make them think and replay with intriguing narratives"
  },
  { 
    id: "reels", 
    name: "Instagram Reels", 
    // Glassmorphism style - foggy/blurry with Instagram gradient accent
    color: "bg-gradient-to-br from-zinc-900/90 via-zinc-900/80 to-purple-950/30 backdrop-blur-xl border border-[#833AB4]/30", 
    hoverColor: "hover:border-[#FD1D1D]/50 hover:shadow-[0_0_40px_rgba(253,29,29,0.15)] hover:from-zinc-900/95",
    textColor: "text-white", 
    accentColor: "#FD1D1D",
    accentTextClass: "bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] bg-clip-text text-transparent",
    duration: "8-12s", 
    style: "Emotional • Save-worthy",
    description: "Create emotional connections that trigger saves"
  },
];

export const TONES = ["urgent", "curious", "dark", "emotional", "absurd"];

export const CATEGORIES = [
  "Absurd Scenarios",
  "What If",
  "Unsettling Truths",
  "Relatable Moments",
  "Visual Curiosity",
];

// AI Tools for Unlimited users
export const AI_VIDEO_TOOLS = [
  { id: "sora", name: "Sora", company: "OpenAI", icon: "🎬" },
  { id: "runway", name: "Runway Gen-3", company: "Runway", icon: "🎥" },
  { id: "pika", name: "Pika Labs", company: "Pika", icon: "⚡" },
  { id: "kling", name: "Kling AI", company: "Kuaishou", icon: "🌟" },
  { id: "luma", name: "Luma Dream Machine", company: "Luma AI", icon: "💫" },
  { id: "minimax", name: "MiniMax Video", company: "MiniMax", icon: "🎞️" },
  { id: "heygen", name: "HeyGen", company: "HeyGen", icon: "🗣️" },
];

export const AI_IMAGE_TOOLS = [
  { id: "midjourney", name: "Midjourney", company: "Midjourney", icon: "🎨" },
  { id: "dalle", name: "DALL-E 3", company: "OpenAI", icon: "🖼️" },
  { id: "stable", name: "Stable Diffusion", company: "Stability AI", icon: "🌀" },
  { id: "leonardo", name: "Leonardo AI", company: "Leonardo", icon: "🦁" },
  { id: "ideogram", name: "Ideogram", company: "Ideogram", icon: "✨" },
  { id: "flux", name: "Flux", company: "Black Forest Labs", icon: "🔥" },
  { id: "firefly", name: "Adobe Firefly", company: "Adobe", icon: "🔶" },
];

// Segment colors based on platform
export const TIKTOK_COLORS = {
  instant_interruption: { bg: "bg-[#25F4EE]/10", border: "border-[#25F4EE]/30", text: "text-[#25F4EE]", dot: "bg-[#25F4EE]" },
  curiosity_lock: { bg: "bg-[#FE2C55]/10", border: "border-[#FE2C55]/30", text: "text-[#FE2C55]", dot: "bg-[#FE2C55]" },
  escalation: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  payoff: { bg: "bg-[#25F4EE]/10", border: "border-[#25F4EE]/30", text: "text-[#25F4EE]", dot: "bg-[#25F4EE]" },
  loop_setup: { bg: "bg-zinc-500/10", border: "border-zinc-500/30", text: "text-zinc-400", dot: "bg-zinc-500" },
  // Long-form segments
  hook: { bg: "bg-[#25F4EE]/10", border: "border-[#25F4EE]/30", text: "text-[#25F4EE]", dot: "bg-[#25F4EE]" },
  context: { bg: "bg-[#FE2C55]/10", border: "border-[#FE2C55]/30", text: "text-[#FE2C55]", dot: "bg-[#FE2C55]" },
  rising_action_1: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  rising_action_2: { bg: "bg-[#25F4EE]/10", border: "border-[#25F4EE]/30", text: "text-[#25F4EE]", dot: "bg-[#25F4EE]" },
  twist_peak: { bg: "bg-[#FE2C55]/10", border: "border-[#FE2C55]/30", text: "text-[#FE2C55]", dot: "bg-[#FE2C55]" },
  falling_action: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  resolution_tease: { bg: "bg-[#25F4EE]/10", border: "border-[#25F4EE]/30", text: "text-[#25F4EE]", dot: "bg-[#25F4EE]" },
  cliffhanger_loop: { bg: "bg-zinc-500/10", border: "border-zinc-500/30", text: "text-zinc-400", dot: "bg-zinc-500" },
};

export const SHORTS_COLORS = {
  intrigue_setup: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
  context_without_answers: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  mental_escalation: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
  loopable_implication: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  // Long-form segments
  intrigue_hook: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
  mystery_setup: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  layer_1: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
  layer_2: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  revelation: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
  deeper_question: { bg: "bg-white/5", border: "border-white/20", text: "text-white", dot: "bg-white" },
  loop_back: { bg: "bg-[#FF0000]/10", border: "border-[#FF0000]/30", text: "text-[#FF0000]", dot: "bg-[#FF0000]" },
};

export const REELS_COLORS = {
  relatable_moment: { bg: "bg-[#833AB4]/10", border: "border-[#833AB4]/30", text: "text-[#833AB4]", dot: "bg-[#833AB4]" },
  emotional_deepening: { bg: "bg-[#FD1D1D]/10", border: "border-[#FD1D1D]/30", text: "text-[#FD1D1D]", dot: "bg-[#FD1D1D]" },
  recognition: { bg: "bg-[#FCAF45]/10", border: "border-[#FCAF45]/30", text: "text-[#FCAF45]", dot: "bg-[#FCAF45]" },
  save_trigger: { bg: "bg-[#833AB4]/10", border: "border-[#833AB4]/30", text: "text-[#833AB4]", dot: "bg-[#833AB4]" },
  // Long-form segments
  emotional_hook: { bg: "bg-[#833AB4]/10", border: "border-[#833AB4]/30", text: "text-[#833AB4]", dot: "bg-[#833AB4]" },
  setup: { bg: "bg-[#FD1D1D]/10", border: "border-[#FD1D1D]/30", text: "text-[#FD1D1D]", dot: "bg-[#FD1D1D]" },
  deepening_1: { bg: "bg-[#FCAF45]/10", border: "border-[#FCAF45]/30", text: "text-[#FCAF45]", dot: "bg-[#FCAF45]" },
  deepening_2: { bg: "bg-[#833AB4]/10", border: "border-[#833AB4]/30", text: "text-[#833AB4]", dot: "bg-[#833AB4]" },
  emotional_peak: { bg: "bg-[#FD1D1D]/10", border: "border-[#FD1D1D]/30", text: "text-[#FD1D1D]", dot: "bg-[#FD1D1D]" },
  resolution_save_trigger: { bg: "bg-[#FCAF45]/10", border: "border-[#FCAF45]/30", text: "text-[#FCAF45]", dot: "bg-[#FCAF45]" },
};

/**
 * Get segment colors based on platform
 */
export function getSegmentColors(platform) {
  switch (platform) {
    case "tiktok":
      return TIKTOK_COLORS;
    case "shorts":
      return SHORTS_COLORS;
    case "reels":
      return REELS_COLORS;
    default:
      return TIKTOK_COLORS;
  }
}

/**
 * Default segment color for unknown segment types
 */
export const DEFAULT_SEGMENT_COLOR = { 
  bg: "bg-zinc-800/50", 
  border: "border-zinc-700", 
  text: "text-zinc-300", 
  dot: "bg-zinc-500" 
};
